import { Text, Colors } from '@amelio/ui-library';

import { CompetencySummarySkeleton } from './CompetencySummarySkeleton';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';

import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText, type LocalizedText } from '@/utils/localization';

interface CompetencySummaryProps {
    proficiencyLevels: ProficiencyLevel[];
    competencyCountByLevel: Record<string, number>;
    isLoading?: boolean;
}

export const CompetencySummary = ({
    proficiencyLevels,
    competencyCountByLevel,
    isLoading,
}: CompetencySummaryProps) => {
    const { t } = useAmelioTranslations();

    if (isLoading) {
        return <CompetencySummarySkeleton />;
    }

    return (
        <div>
            <Text
                size={'small'}
                weight={'semibold'}
                color={Colors.BASE_600}
                className={'mb-[16px] text-[12px] tracking-wide uppercase'}
            >
                {t('postReviewResults.tabs.competencies')}
            </Text>
            <div
                className={
                    'grid !grid-cols-1 gap-[12px] sm:!grid-cols-2 sm:gap-[16px] md:!grid-cols-3 lg:!grid-cols-5 lg:gap-[24px]'
                }
            >
                {proficiencyLevels.map((level, index) => (
                    <CompetencyTile
                        key={level.id ?? `level-${index}`}
                        label={level.label}
                        count={competencyCountByLevel[level.id ?? ''] || 0}
                        color={level.iconColor ?? ''}
                    />
                ))}
            </div>
        </div>
    );
};

CompetencySummary.displayName = 'CompetencySummary';

interface CompetencyTileProps {
    label: LocalizedText;
    count: number;
    color: string;
}

const CompetencyTile = ({ label, count, color }: CompetencyTileProps) => {
    return (
        <div
            className={
                'flex flex-col gap-[8px] rounded-[8px] border border-[var(--tile-color-light)] bg-gradient-to-br from-white/60 to-white/40 p-[16px] shadow-xs transition-shadow'
            }
            style={
                {
                    '--tile-color': color,
                    '--tile-color-light': `${color}33`,
                } as React.CSSProperties
            }
        >
            <span
                className={
                    'text-font-xs font-semibold tracking-wide text-[var(--tile-color)] uppercase'
                }
            >
                {getLocalizedText(label)}
            </span>
            <span className={'text-font-3xl font-bold text-[var(--tile-color)]'}>{count}</span>
        </div>
    );
};
